import { convertEmptyStringsToNull, convertIncomingPercentage, convertOutgoingPercentage } from "@shared/modulePayload";
import {
	StorageEntryData,
	StorageEntryResponse,
	storageEntryResponseSchema,
} from "@features/modules/storage/types";
import { EntryCreatePayload, EntryDeletePayload, EntryUpdatePayload, ParentUpdatePayload, ParentResponse, ParentPayload } from "../../../types/modulesInterfaces";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

// Define constants for the module
const MODULE_TYPE = "Storage";
const TAG: TagTypes = "Storages";
const ENDPOINT = FEModulesObj[FEModules.Storage].path;

const ENTRY_MODULE_TYPE = "StorageEntries";
const TAG_ENTRY: TagTypes = "StorageEntries";
const TAG_ENTRY_DEFAULTS: TagTypes = "StorageEntriesDefaults";
export const ENTRY_ENDPOINT = "storage-entries";

const storageApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const transformEntriesResponse = (response: unknown) => {
	const arr = Array.isArray(response) ? response : [];
	const validated: StorageEntryData[] = arr.map((entry, i) => {
		const parsed = storageEntryResponseSchema.safeParse(entry);
		if (!parsed.success) {
			throw new Error(
				`Storage entry API response validation failed (entry ${i}): ${parsed.error.message}`
			);
		}
		return parsed.data as StorageEntryData;
	});
	validated.forEach((entry) => convertIncomingPercentage(entry, ["transmission_loss_t2"]));
	return validated.sort((a, b) => a.id - b.id);
};

const storageEntriesApiSlice = createEntriesGetApi<StorageEntryData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

const publicStorageApiSlice = createModuleGetApi<ParentResponse>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
});

const publicStorageEntriesApiSlice = createEntriesGetApi<StorageEntryData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformEntriesResponse,
});

export const { useGetStorageQuery } = storageApiSlice;
export const { useGetStorageEntriesQuery, useGetStorageEntriesDefaultsQuery } = storageEntriesApiSlice;

export const { useGetStorageQuery: usePublicGetStorageQuery } = publicStorageApiSlice;
export const { 
	useGetStorageEntriesQuery: usePublicGetStorageEntriesQuery, 
	useGetStorageEntriesDefaultsQuery: usePublicGetStorageEntriesDefaultsQuery 
} = publicStorageEntriesApiSlice;

export const mutationApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateStorages: builder.mutation<ParentResponse, ParentUpdatePayload<ParentPayload>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				return { url: `${ENDPOINT}/${id}/`, method: 'PUT', body: rest };
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'Storages', id: body?.id ?? 0 },
				{ type: "ModulesStatuses", id: activityId },
				{ type: 'ModuleResults', id: body?.id ? `storages-${body.id}` : undefined },
				{ type: 'ModuleHistory', id: body?.id ? `storages-${body.id}` : undefined },
			]
		}),
		updateStorageEntry: builder.mutation<StorageEntryResponse, EntryUpdatePayload<StorageEntryResponse>>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				convertEmptyStringsToNull(rest);
				convertOutgoingPercentage(rest, ["transmission_loss_t2"]);
				return {
					url: `${ENTRY_ENDPOINT}/${id}/`,
					method: 'PUT',
					body: rest
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: 'StorageEntries', id: activityId },
				{ type: 'StorageEntriesDefaults', id: body?.id ? body?.id : undefined },
			]
		}),
		createStorageEntry: builder.mutation<StorageEntryResponse, EntryCreatePayload>({
			query: ({ activityId, parent }) => ({ 
				url: `${ENTRY_ENDPOINT}/`, 
				method: 'POST', 
				body: { activity: activityId, parent } 
			}),
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: 'StorageEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `storages-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `storages-${parent}` : undefined },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
		deleteStorageEntry: builder.mutation<void, EntryDeletePayload>({
			query: ({ id }) => ({ url: `${ENTRY_ENDPOINT}/${id}/`, method: 'DELETE' }),
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: 'StorageEntries', id: activityId },
				{ type: 'ModuleResults', id: parent ? `storages-${parent}` : undefined },
				{ type: 'ModuleHistory', id: parent ? `storages-${parent}` : undefined },
				{ type: 'StorageEntriesDefaults', id },
				{ type: 'ModulesStatuses', id: activityId }
			]
		}),
	}),
});

export const {
	useUpdateStoragesMutation,
	useCreateStorageEntryMutation,
	useUpdateStorageEntryMutation,
	useDeleteStorageEntryMutation,
} = mutationApiSlice;

export const storageEndpoints = storageApiSlice.endpoints;
export const storageEntriesEndpoints = storageEntriesApiSlice.endpoints;
export const storageMutationEndpoints = mutationApiSlice.endpoints;